import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, CreditCard, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Pagination } from '@/components/ui/pagination';
import { Skeleton } from '@/components/ui/skeleton';
import type { BookingSearchModel } from '@/features/manage-bookings/bookings.api';
import {
  BookingAmount,
  PaymentCategoryCell,
} from '@/features/manage-bookings/payment-category';
import { BOOKING_STATUS, type AdminBookingRow } from '@/features/manage-bookings/types';
import { Meter } from '@/components/ui/stat-tile';
import { formatCurrency, formatDateTime, formatNumber, formatPhone } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useEventTimeslots } from './sales.api';
import { fillPercent, type BookingTimeslot } from './types';

/**
 * The timeslots of one event, each opening again to the people booked into it.
 *
 * Two levels of disclosure, both of them explicit: a slot states its booking
 * count on the button, so "Bookings (12)" says what you get before you open it,
 * and a slot with none renders no button rather than an empty one.
 */

const BOOKINGS_PAGE_SIZE = 8;

function StatusBadge({ row }: { row: AdminBookingRow }) {
  const { t } = useTranslation();

  return (
    <>
      {row.status === BOOKING_STATUS.confirmed && (
        <Badge variant="success">{t('sales.confirmed')}</Badge>
      )}
      {row.status === BOOKING_STATUS.pending && (
        <Badge variant="warning">{t('sales.pending')}</Badge>
      )}
      {row.status === BOOKING_STATUS.cancelled && (
        <Badge variant="destructive">{t('sales.cancelled')}</Badge>
      )}
      {row.checkedInAt && <Badge variant="success">{t('sales.checkedIn')}</Badge>}
    </>
  );
}

/** One timeslot, with its bookings behind a counted disclosure. */
function TimeslotCard({
  slot,
  onViewBooking,
  onAddBooking,
  onRecordPayment,
}: {
  slot: BookingTimeslot;
  onViewBooking: (id: string) => void;
  onAddBooking: (slot: BookingTimeslot) => void;
  onRecordPayment: (booking: AdminBookingRow) => void;
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);

  const bookings = slot.bookings ?? [];
  const pageCount = Math.ceil(bookings.length / BOOKINGS_PAGE_SIZE);
  const visible = bookings.slice(
    page * BOOKINGS_PAGE_SIZE,
    (page + 1) * BOOKINGS_PAGE_SIZE
  );

  const fill = fillPercent(slot.fillRate);

  return (
    <div
      className={cn(
        'bg-card overflow-hidden rounded-xl border',
        (slot.awaitingPayment ?? 0) > 0 && 'border-warning/50'
      )}
    >
      <div className="space-y-3 p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium">
              {formatDateTime(slot.startTime)}
            </p>
            {slot.hostName && (
              <p className="text-muted-foreground text-xs">{slot.hostName}</p>
            )}
          </div>
          <div className="shrink-0 text-right">
            <p className="font-semibold tabular-nums">
              {formatCurrency(slot.collected)}
            </p>
            {(slot.benefitValueRedeemed ?? 0) > 0 && (
              <p className="text-muted-foreground text-xs tabular-nums">
                {formatCurrency(slot.benefitValueRedeemed)}{' '}
                {t('sales.inBenefits')}
              </p>
            )}
          </div>
        </div>

        <Meter
          label={`${t('sales.nOfMSlots', {
            bookings: formatNumber(slot.totalBookings),
            slots: formatNumber(slot.maxCapacity),
          })} · ${
            (slot.spotsLeft ?? 0) > 0
              ? t('sales.nSpotsLeft', { count: slot.spotsLeft ?? 0 })
              : t('sales.full')
          }`}
          value={`${fill}%`}
          percent={fill}
        />

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div>
            <p className="text-muted-foreground text-xs">
              {t('sales.bookings')}
            </p>
            <p className="text-sm tabular-nums">
              {formatNumber(slot.totalBookings)}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs">
              {t('sales.checkedIn')}
            </p>
            <p className="text-sm tabular-nums">
              {formatNumber(slot.checkedIn)}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs">
              {t('sales.usedABenefit')}
            </p>
            <p className="text-sm tabular-nums">
              {formatNumber(slot.usedBenefit)}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs">
              {t('sales.awaitingPayment')}
            </p>
            <p className="text-sm tabular-nums">
              {formatNumber(slot.awaitingPayment)}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => onAddBooking(slot)}>
            <Plus className="size-3.5" />
            {t('sales.addBooking')}
          </Button>
          {bookings.length > 0 && (
            <Collapsible
              open={open}
              onOpenChange={(next) => {
                setOpen(next);
                if (!next) setPage(0);
              }}
              className="contents"
            >
              <CollapsibleTrigger asChild>
                <Button variant="outline" size="sm">
                  <ChevronDown
                    className={cn(
                      'size-3.5 transition-transform',
                      open && 'rotate-180'
                    )}
                  />
                  {t('sales.bookings')} ({formatNumber(bookings.length)})
                </Button>
              </CollapsibleTrigger>
            </Collapsible>
          )}
        </div>
      </div>

      {open && bookings.length > 0 && (
        <div className="bg-muted/40 divide-y border-t">
          {visible.map((row) => (
            <div key={row.id} className="space-y-2 p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 space-y-1">
                  <p className="text-sm font-medium break-words">
                    {[row.firstName, row.lastName].filter(Boolean).join(' ')}
                  </p>
                  <p className="text-muted-foreground text-xs break-words">
                    {row.email}
                  </p>
                  {row.phoneNumber && (
                    <p className="text-muted-foreground text-xs tabular-nums">
                      {formatPhone(row.phoneNumber)}
                    </p>
                  )}
                  {/* One flowing row: as separate blocks the status and the
                      payment category each claimed a line, so a cancelled and
                      refunded booking burned three lines on two tags. */}
                  <div className="flex flex-wrap items-center gap-1.5">
                    <StatusBadge row={row} />
                    <PaymentCategoryCell row={row} language={language} />
                  </div>
                </div>
                <div className="shrink-0 text-right text-sm">
                  <BookingAmount row={row} />
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {(row.outstanding ?? 0) > 0 && (
                  <Button size="sm" onClick={() => onRecordPayment(row)}>
                    <CreditCard className="size-3.5" />
                    {t('sales.recordPayment')}
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onViewBooking(row.id)}
                >
                  {t('sales.view')}
                </Button>
              </div>
            </div>
          ))}

          {pageCount > 1 && (
            <Pagination
              className="p-3"
              page={page}
              size={BOOKINGS_PAGE_SIZE}
              total={bookings.length}
              onPageChange={setPage}
            />
          )}
        </div>
      )}
    </div>
  );
}

export function EventTimeslots({
  eventId,
  model,
  onViewBooking,
  onAddBooking,
  onRecordPayment,
}: {
  eventId: string;
  model: BookingSearchModel;
  onViewBooking: (id: string) => void;
  onAddBooking: (slot: BookingTimeslot) => void;
  onRecordPayment: (booking: AdminBookingRow) => void;
}) {
  const { t } = useTranslation();
  const query = useEventTimeslots(eventId, model);
  const timeslots = query.data ?? [];

  if (query.isPending) {
    return (
      <div className="space-y-2 p-3">
        {Array.from({ length: 2 }).map((_, index) => (
          <div key={index} className="bg-card space-y-3 rounded-xl border p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1.5">
                <Skeleton className="h-4 w-36" />
                <Skeleton className="h-3 w-24" />
              </div>
              <Skeleton className="h-5 w-16" />
            </div>
            <Skeleton className="h-2 w-full rounded-full" />
          </div>
        ))}
      </div>
    );
  }

  if (query.isError) {
    return (
      <p className="text-muted-foreground py-10 text-center text-sm">
        {t('sales.timeslotsFailed')}
      </p>
    );
  }

  if (timeslots.length === 0) {
    return (
      <p className="text-muted-foreground py-10 text-center text-sm">
        {t('sales.noTimeslots')}
      </p>
    );
  }

  // A booking added or paid refetches the slots with the old ones on screen.
  return (
    <div className="relative">
      <BusyOverlay show={query.isFetching} />
      <div
        className={cn(
          'space-y-2 p-3',
          query.isFetching && 'opacity-60 transition-opacity'
        )}
      >
        {timeslots.map((slot) => (
          <TimeslotCard
            key={slot.id}
            slot={slot}
            onViewBooking={onViewBooking}
            onAddBooking={onAddBooking}
            onRecordPayment={onRecordPayment}
          />
        ))}
      </div>
    </div>
  );
}
